import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Table, Space, Button, Tag, Select, App, Tooltip, Result, theme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CheckCircleOutlined, CloseCircleOutlined, EyeOutlined, LinkOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { useAuth } from '../store/AuthContext';
import { formatDateTime, formatDateTimeCompact } from '../utils/datetime';

const { Title, Text } = Typography;

interface TicketStepResult {
  name: string;
  success: boolean;
  latency: number;
  error?: string;
}

interface TicketDetail {
  serverKey: string;
  serverName: string;
  ip: string;
  state: string;
  failedStep: string;
  error?: string;
  steps?: TicketStepResult[];
  totalDuration?: number;
  latency?: number;
}

interface Ticket {
  id: string;
  title: string;
  errorType: string;
  country: string;
  affectedServers: string[];
  details: TicketDetail[];
  status: string;
  createdAt: string;
  updatedAt: string;
}

const statusColors: Record<string, string> = {
  open: 'red',
  acknowledged: 'gold',
  resolved: 'green',
};

const errorTypeLabels: Record<string, string> = {
  network: 'Network (Ping)',
  visibility: 'Visibility (Global)',
  proxy_connection: 'Proxy Connection',
  proxy_request: 'Proxy Request',
  proxy_latency: 'Proxy Tunnel Latency',
  connectivity: 'Connectivity',
  protocol: 'Protocol',
  traffic: 'Traffic',
  performance: 'Performance',
  unknown: 'Unknown',
};

const errorTypeColors: Record<string, string> = {
  network: 'red',
  visibility: 'orange',
  proxy_connection: 'magenta',
  proxy_request: 'volcano',
  proxy_latency: 'cyan',
  connectivity: 'red',
  protocol: 'purple',
  traffic: 'gold',
  performance: 'blue',
  unknown: 'default',
};

const stateColors: Record<string, string> = {
  DOWN: 'red',
  ERROR: 'volcano',
  ZOMBIE: 'purple',
  DEGRADED: 'orange',
  SUSPECT: 'gold',
  HEALTHY: 'green',
  UNKNOWN: 'default',
};

const stepLabels: Record<string, string> = {
  icmp_ping: 'ICMP Ping',
  globalping: 'Global Visibility',
  proxy_connect: 'Proxy TCP Connect',
  proxy_request: 'Proxy HTTP Request',
  proxy_latency: 'Tunnel Latency',
  tcp_connect: 'TCP Connect',
  protocol_handshake: 'Protocol Handshake',
  traffic_test: 'Traffic Test',
  performance_test: 'Performance Test',
};

const TicketsPage: React.FC = () => {
  const { message } = App.useApp();
  const { user } = useAuth();
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [countryFilter, setCountryFilter] = useState<string | undefined>();
  const [errorTypeFilter, setErrorTypeFilter] = useState<string | undefined>();
  const [ticketsEnabled, setTicketsEnabled] = useState<boolean | null>(null);

  const canWrite = user?.role === 'admin' || user?.role === 'operator';

  useEffect(() => {
    apiClient.get('/features')
      .then((res) => setTicketsEnabled(res.data?.data?.ticketsEnabled ?? false))
      .catch(() => setTicketsEnabled(false));
  }, []);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = { page: String(page), limit: String(pageSize) };
      if (statusFilter) params.status = statusFilter;
      if (countryFilter) params.country = countryFilter;
      if (errorTypeFilter) params.errorType = errorTypeFilter;
      const { data } = await apiClient.get('/tickets', { params });
      setTickets(data.data.items || []);
      setTotal(data.data.total);
    } catch {
      message.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, statusFilter, countryFilter, errorTypeFilter, message]);

  useEffect(() => {
    if (ticketsEnabled) fetchTickets();
  }, [fetchTickets, ticketsEnabled]);

  const updateStatus = async (id: string, status: string) => {
    try {
      await apiClient.put(`/tickets/${id}`, { status });
      message.success(`Ticket ${status}`);
      fetchTickets();
    } catch {
      message.error('Failed to update ticket');
    }
  };

  const columns: ColumnsType<Ticket> = [
    {
      title: 'Error Type', dataIndex: 'errorType', key: 'errorType', width: 165,
      ellipsis: { showTitle: false },
      render: (v: string) => (
        <Tooltip title={errorTypeLabels[v] || v}>
          <Tag color={errorTypeColors[v] || 'default'} style={{ margin: 0 }}>{errorTypeLabels[v] || v}</Tag>
        </Tooltip>
      ),
    },
    { title: 'Country', dataIndex: 'country', key: 'country', width: 75 },
    {
      title: 'Affected', key: 'affected', width: 105,
      render: (_: unknown, r: Ticket) => {
        const serverCount = r.affectedServers?.length || 0;
        const detailCount = r.details?.length || 0;
        return (
          <Tooltip title={`${serverCount} server(s), ${detailCount} issue(s)`}>
            <Text style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
              {serverCount} server{serverCount !== 1 ? 's' : ''}
              {detailCount > serverCount && <Text type="secondary" style={{ fontSize: 12 }}> / {detailCount} IPs</Text>}
            </Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'Status', key: 'status', width: 105,
      render: (_: unknown, r: Ticket) => (
        <Tag color={statusColors[r.status] || 'default'} style={{ margin: 0 }}>{r.status.toUpperCase()}</Tag>
      ),
    },
    // Both timestamps stay, but in the short local form with the full one on
    // hover — a ticket list is read by "when did this start / when did it last
    // move", not by the second.
    {
      title: 'Created', dataIndex: 'createdAt', key: 'createdAt', width: 120,
      render: (v: string) => (
        <Tooltip title={formatDateTime(v)}>
          <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{formatDateTimeCompact(v)}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Updated', dataIndex: 'updatedAt', key: 'updatedAt', width: 120,
      render: (v: string) => (
        <Tooltip title={formatDateTime(v)}>
          <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{formatDateTimeCompact(v)}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Actions', key: 'actions', width: 125,
      render: (_: unknown, r: Ticket) => (
        <Space size={4}>
          {canWrite && r.status === 'open' && (
            <Button size="small" icon={<EyeOutlined />} onClick={() => updateStatus(r.id, 'acknowledged')}>
              Acknowledge
            </Button>
          )}
          {canWrite && r.status === 'acknowledged' && (
            <Button size="small" icon={<CheckCircleOutlined />} onClick={() => updateStatus(r.id, 'resolved')}>
              Resolve
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const expandedRow = (record: Ticket) => {
    const detailItems = record.details || [];
    if (detailItems.length === 0) {
      // Fallback for old tickets without details
      return (
        <div style={{ padding: '8px 0' }}>
          <Text type="secondary">Affected servers: </Text>
          <Space wrap>
            {record.affectedServers?.map((key) => (
              <Button
                key={key}
                type="link"
                size="small"
                icon={<LinkOutlined />}
                onClick={(e) => { e.stopPropagation(); navigate(`/servers/${key}/health`); }}
              >
                {key}
              </Button>
            ))}
          </Space>
        </div>
      );
    }

    // Group details by serverKey
    const byServer = new Map<string, TicketDetail[]>();
    for (const d of detailItems) {
      const existing = byServer.get(d.serverKey) || [];
      existing.push(d);
      byServer.set(d.serverKey, existing);
    }

    return (
      <div style={{ padding: '4px 0' }}>
        {Array.from(byServer.entries()).map(([serverKey, serverDetails]) => {
          const serverName = serverDetails[0]?.serverName || serverKey;
          const detailColumns: ColumnsType<TicketDetail> = [
            {
              title: 'IP / Proxy', dataIndex: 'ip', key: 'ip', width: 165,
              ellipsis: { showTitle: false },
              render: (ip: string) => (
                <Tooltip title={ip}>
                  <Text code style={{ fontSize: 12 }}>{ip}</Text>
                </Tooltip>
              ),
            },
            {
              title: 'State', dataIndex: 'state', key: 'state', width: 90,
              render: (s: string) => <Tag color={stateColors[s] || 'default'} style={{ margin: 0 }}>{s}</Tag>,
            },
            {
              title: 'Failed Step', dataIndex: 'failedStep', key: 'failedStep', width: 155,
              ellipsis: { showTitle: false },
              render: (s: string) => s ? (
                <Tooltip title={stepLabels[s] || s}>
                  <Tag color="error" style={{ margin: 0 }}>
                    {stepLabels[s] || s}
                  </Tag>
                </Tooltip>
              ) : <Text type="secondary">-</Text>,
            },
            {
              // The step list is the reason this row is expanded at all, so it
              // keeps its lines — just tighter ones (11px / 16px leading).
              title: 'Check Steps', key: 'steps', width: 235,
              render: (_: unknown, d: TicketDetail) => {
                if (!d.steps || d.steps.length === 0) return <Text type="secondary">-</Text>;
                return (
                  <div style={{ fontSize: 12, lineHeight: '18px' }}>
                    {d.steps.map((s) => (
                      <div key={s.name} style={{ whiteSpace: 'nowrap' }}>
                        {s.success
                          ? <CheckCircleOutlined style={{ color: token.colorSuccess, marginRight: 4 }} />
                          : <CloseCircleOutlined style={{ color: token.colorError, marginRight: 4 }} />}
                        {stepLabels[s.name] || s.name}: {s.latency > 0 ? `${s.latency.toFixed(0)}ms` : '--'}
                      </div>
                    ))}
                    {d.totalDuration != null && d.totalDuration > 0 && (
                      <Text type="secondary" style={{ fontSize: 12 }}>Total: {d.totalDuration.toFixed(0)}ms</Text>
                    )}
                  </div>
                );
              },
            },
            {
              title: 'Error', dataIndex: 'error', key: 'error', ellipsis: { showTitle: false },
              render: (e: string) => e ? (
                <Tooltip title={e}>
                  <Text type="danger" style={{ fontSize: 12 }}>{e}</Text>
                </Tooltip>
              ) : <Text type="secondary">-</Text>,
            },
          ];

          return (
            <div key={serverKey} style={{ marginBottom: 10 }}>
              <Space align="center" size={6} style={{ marginBottom: 2 }}>
                <Button
                  type="link"
                  size="small"
                  icon={<LinkOutlined />}
                  onClick={(e) => { e.stopPropagation(); navigate(`/servers/${serverKey}/health`); }}
                  style={{ padding: 0, fontWeight: 600, fontSize: 13 }}
                >
                  {serverName}
                </Button>
                <Text type="secondary" copyable={{ text: serverKey }} style={{ fontSize: 12 }}>
                  {serverKey}
                </Text>
              </Space>
              <Table
                dataSource={serverDetails}
                rowKey={(d) => `${d.serverKey}-${d.ip}-${d.failedStep}`}
                size="small"
                pagination={false}
                style={{ marginLeft: 8 }}
                columns={detailColumns}
              />
            </div>
          );
        })}
      </div>
    );
  };

  if (ticketsEnabled === false) {
    return (
      <>
        <Title level={4} style={{ marginBottom: 8 }}>Tickets</Title>
        <Result
          status="info"
          title="Ticket system is disabled"
          subTitle="An administrator can enable it in Settings."
          extra={
            user?.role === 'admin' ? (
              <Button type="primary" onClick={() => navigate('/settings#system')}>
                Go to Settings
              </Button>
            ) : undefined
          }
        />
      </>
    );
  }

  return (
    <>
      {/* Title and the three filters share one line — the filter row used to
          eat a whole line of an 800px-tall viewport on its own. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        flexWrap: 'wrap', marginBottom: 10,
      }}>
        <Title level={4} style={{ margin: 0 }}>Tickets</Title>
        <div style={{ flex: 1 }} />
        <Select
          placeholder="Status" allowClear size="small" style={{ width: 130 }}
          onChange={(v) => { setStatusFilter(v); setPage(1); }}
          options={[
            { value: 'open', label: 'Open' },
            { value: 'acknowledged', label: 'Acknowledged' },
            { value: 'resolved', label: 'Resolved' },
          ]}
        />
        <Select
          placeholder="Country" allowClear size="small" style={{ width: 110 }}
          showSearch
          onChange={(v) => { setCountryFilter(v); setPage(1); }}
        />
        <Select
          placeholder="Error Type" allowClear size="small" style={{ width: 170 }}
          onChange={(v) => { setErrorTypeFilter(v); setPage(1); }}
          options={[
            { value: 'network', label: 'Network (Ping)' },
            { value: 'visibility', label: 'Visibility (Global)' },
            { value: 'proxy_connection', label: 'Proxy Connection' },
            { value: 'proxy_request', label: 'Proxy Request' },
            { value: 'proxy_latency', label: 'Proxy Tunnel Latency' },
            { value: 'connectivity', label: 'Connectivity' },
            { value: 'protocol', label: 'Protocol' },
            { value: 'traffic', label: 'Traffic' },
            { value: 'performance', label: 'Performance' },
          ]}
        />
      </div>

      <Table
        dataSource={tickets}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="small"
        expandable={{
          expandedRowRender: expandedRow,
          rowExpandable: (r) => (r.details?.length > 0 || r.affectedServers?.length > 0),
        }}
        pagination={{
          current: page,
          total,
          pageSize,
          size: 'small',
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          showTotal: (t, range) => `${range[0]}-${range[1]} of ${t}`,
        }}
      />
    </>
  );
};

export default TicketsPage;
